// ListProducts.js
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { CSVLink } from "react-csv";
import { TableSortLabel } from "@mui/material";
import { useFirestore } from "../../firebase/FirestoreContext";
import config from "../../../config";
import { EnvContext } from "../../../context/EnvContext";
import { OpenInNew } from "@mui/icons-material";

const ListProducts = () => {
  const db = useFirestore();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [productReports, setProductReports] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: "product_name",
    direction: "asc",
  });
  const environment = useContext(EnvContext);
  const collectionPath = environment === "staging" ? config.enveironment.staging.collectionPath : config.enveironment.production.collectionPath;

  // Fetch products along with sold_count from orders
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productsCollectionRef = collection(db, `${collectionPath}Products/ProductsList/DataBase`);
        const productsSnapshot = await getDocs(productsCollectionRef);

        // Build a product map with sold_count initialized to 0
        const productsMap = {};
        productsSnapshot.docs.forEach((doc) => {
          const prod = { id: doc.id, ...doc.data(), sold_count: 0 };
          productsMap[doc.id] = prod;
        });

        // Fetch orders and update sold_count per product
        const ordersCollectionRef = collection(db, `${collectionPath}Orders/OrdersList/DataBase`);
        const ordersSnapshot = await getDocs(ordersCollectionRef);
        ordersSnapshot.docs.forEach((orderDoc) => {
          const orderData = orderDoc.data();
          if (Array.isArray(orderData.items)) {
            orderData.items.forEach((item) => {
              if (item.product_id && productsMap[item.product_id]) {
                productsMap[item.product_id].sold_count += item.quantity || 0;
              }
            });
          }
        });

        setProducts(Object.values(productsMap));
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    fetchProducts();
  }, [db, collectionPath]);

  // Fetch reports count for each product
  useEffect(() => {
    const fetchProductReports = async () => {
      const reports = {};
      for (const product of products) {
        try {
          const reviewsCollectionRef = collection(db, `${collectionPath}Products/ProductsList/DataBase/${product.id}/Reviews`);
          const reviewsSnapshot = await getDocs(reviewsCollectionRef);
          let count = 0;
          for (const reviewDoc of reviewsSnapshot.docs) {
            try {
              const reportsCollectionRef = collection(db, `${collectionPath}Products/ProductsList/DataBase/${product.id}/Reviews/${reviewDoc.id}/Reports`);
              const reportsSnapshot = await getDocs(reportsCollectionRef);
              reportsSnapshot.forEach((reportDoc) => {
                const reportData = reportDoc.data();
                if (reportData && !reportData.checked) {
                  count++;
                }
              });
            } catch (error) {
              console.error(`Error fetching reports for review ${reviewDoc.id}:`, error);
            }
          }
          reports[product.id] = count;
        } catch (error) {
          console.error(`Error fetching reviews for product ${product.id}:`, error);
          reports[product.id] = 0;
        }
      }
      setProductReports(reports);
    };

    if (products.length > 0) {
      fetchProductReports();
    }
  }, [db, collectionPath, products]);

  const formatUID = (uid, totalLength = 8) => String(uid).padStart(totalLength, "0");

  const handleSortRequest = (property) => {
    const isAsc = sortConfig.key === property && sortConfig.direction === "asc";
    setSortConfig({ key: property, direction: isAsc ? "desc" : "asc" });
  };

  const sortedProducts = [...products].sort((a, b) => {
    return (a.brand_name || "").localeCompare(b.brand_name || "") || (a.product_name || "").localeCompare(b.product_name || "");
  });

  const generateCsvData = () => {
    const headers = [
      { label: "Product ID", key: "product_uid" },
      { label: "Name", key: "product_name" },
      { label: "Brand", key: "brand_name" },
      { label: "Category", key: "product_category" },
      { label: "Description", key: "product_description" },
      { label: "Potency Value", key: "potency_value" },
      { label: "Key Ingredients", key: "key_ingredients" },
      { label: "Net Weight", key: "net_weight" },
      { label: "Weight Units", key: "weight_units" },
      { label: "Effects", key: "effects" },
      { label: "Strength", key: "strength" },
      { label: "Inactive", key: "inactive" },
    ];

    const csvData = products.map((product) => ({
      product_uid: product.product_uid || "",
      product_name: product.product_name || "",
      brand_name: product.brand_name || "",
      product_category: product.product_category || "",
      product_description: product.product_description || "",
      potency_value: product.potency_value || "",
      key_ingredients: product.key_ingredients || "",
      net_weight: product.net_weight || "",
      weight_units: product.weight_units || "",
      effects: product.effects || "",
      strength: product.strength || "",
      inactive: product.inactive ? "Yes" : "No",
    }));

    return { headers, data: csvData };
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2>All Products</h2>
        <CSVLink {...generateCsvData()} filename="products_list.csv" style={styles.csvLink}>
          Download Products as CSV
        </CSVLink>
      </div>
      <table style={styles.table}>
        <thead>
          <tr style={styles.tableRowHeader}>
            <th style={{ ...styles.th, width: "120px" }}>Product UID</th>
            <th style={styles.th}>
              <TableSortLabel
                active={sortConfig.key === "product_name"}
                direction={sortConfig.key === "product_name" ? sortConfig.direction : "asc"}
                onClick={() => handleSortRequest("product_name")}
              >
                Name
              </TableSortLabel>
            </th>
            <th style={styles.th}>
              <TableSortLabel
                active={sortConfig.key === "brand"}
                direction={sortConfig.key === "brand" ? sortConfig.direction : "asc"}
                onClick={() => handleSortRequest("brand")}
              >
                Brand
              </TableSortLabel>
            </th>
            <th style={styles.th}>
              <TableSortLabel
                active={sortConfig.key === "product_category"}
                direction={sortConfig.key === "product_category" ? sortConfig.direction : "asc"}
                onClick={() => handleSortRequest("product_category")}
              >
                Category
              </TableSortLabel>
            </th>
            <th style={styles.th}>Sold</th>
            <th style={styles.th}>Picked</th>
            <th style={styles.th}>Reports</th>
          </tr>
        </thead>
        <tbody>
          {sortedProducts.map((product) => (
            <tr key={product.id} style={styles.clickableRow}>
              <td style={{ ...styles.td, display: "flex", justifyContent: "center", alignItems: "center" }}>
                <a style={{ color: "black" }} href={`/main/products/edit/${product.id}`}>
                  {`P${formatUID(product.product_uid)}`}
                </a>
                <a style={{ marginLeft: "5px" }} href={`/main/products/edit/${product.id}`} target="_blank" rel="noopener noreferrer">
                  <OpenInNew style={{ fontSize: 14, marginLeft: 5, color: "black" }} />
                </a>
              </td>
              <td style={{ ...styles.td, fontWeight: "500" }}>{product.product_name}</td>
              <td style={styles.td}>
                {product.brand} <span style={{ color: "silver" }}>{product.brand_name}</span>
              </td>
              <td style={{ ...styles.td, color: "silver" }}>{product.product_category}</td>
              <td style={styles.td}>{product.sold_count || 0}</td>
              <td style={{ ...styles.td, textAlign: "center" }}>
                {product.staff_pick && (
                  <span role="img" aria-label="star">
                    ⭐
                  </span>
                )}
              </td>
              <td style={styles.td}>
                {productReports[product.id] ? (
                  <div style={{ color: "red", fontWeight: "700" }}>{productReports[product.id]}</div>
                ) : (
                  <span style={{ color: "silver" }}>0</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const styles = {
  container: {
    padding: 20,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  csvLink: {
    padding: "10px 20px",
    color: "black",
    textDecoration: "none",
    borderRadius: "5px",
    border: "1px solid #ccc",
  },
  table: {
    borderCollapse: "collapse",
    width: "100%",
  },
  tableRowHeader: {
    backgroundColor: "rgba(49,57,66,0.1)",
  },
  th: {
    border: "1px solid #d1d0d0",
    padding: "8px",
    textAlign: "left",
    fontWeight: "700",
    color: "black",
  },
  td: {
    border: "1px solid #d1d0d0",
    padding: "8px",
    color: "gray",
  },
  clickableRow: {
    cursor: "pointer",
  },
};

export default ListProducts;
